/**
 * "İlerlemeni kaydet": the phone login dialog (DOM over the canvas, so the phone keyboard and SMS autofill work),
 * shared by every game and the launcher. The flow and copy are enjoyvent's panel login: phone → 4-digit code →
 * signed in. Optional, never a wall: "Şimdi değil" closes it and play goes on as a guest.
 *
 * H9: the phone field opens only after "13 yaşından büyüğüm" is ticked (the API also requires it).
 * H7/H10: the WhatsApp box is never pre-ticked or required, and turning it off later costs nothing.
 */
import type { ApiErrorCode } from "@enjoyment/contracts";
import { pageAccount, type Account, type AccountState } from "./account";
import { codeDigits, displayPhone, formatNational, isTurkishMobile, nationalDigits } from "./phone";
import "./account.css";

type Lang = "tr" | "en";

const STRINGS = {
  title: { tr: "İlerlemeni kaydet", en: "Save your progress" },
  lead: {
    tr: "Telefon numaranla giriş yap; seviyelerin ve yıldızların başka telefonda ya da tarayıcıda da seninle olsun.",
    en: "Sign in with your phone number to keep your levels and stars on any phone or browser.",
  },
  age: { tr: "13 yaşından büyüğüm", en: "I'm 13 or older" },
  ageHint: {
    tr: "Giriş 13 yaş ve üstü içindir. Giriş yapmadan da her oyunu oynayabilirsin; ilerlemen bu cihazda kalır.",
    en: "Sign-in is for ages 13 and up. You can play every game without it; your progress stays on this device.",
  },
  phone: { tr: "Telefon", en: "Phone" },
  whatsapp: { tr: "WhatsApp'tan yeni oyun ve etkinlik haberlerini almak istiyorum", en: "Send me news about new games and events on WhatsApp" },
  continue: { tr: "Devam Et", en: "Continue" },
  privacy: {
    tr: "Numaran şifreli saklanır ve kimseyle paylaşılmaz; yalnız giriş için ve izin verirsen WhatsApp'tan yazmak için kullanılır.",
    en: "Your number is stored encrypted and never shared; it's only used to sign you in and, if you agree, to message you on WhatsApp.",
  },
  notNow: { tr: "Şimdi değil", en: "Not now" },
  close: { tr: "Kapat", en: "Close" },
  change: { tr: "Değiştir", en: "Change" },
  otpHint: { tr: "Telefonuna gönderilen 4 haneli kodu gir", en: "Enter the 4-digit code sent to your phone" },
  digit: { tr: "Kodun {n}. hanesi", en: "Code digit {n}" },
  resend: { tr: "Yeni Kod Gönder", en: "Resend Code" },
  seconds: { tr: "sn", en: "s" },
  signIn: { tr: "Giriş Yap", en: "Sign In" },
  sending: { tr: "Gönderiliyor...", en: "Sending..." },
  wait: { tr: "Lütfen bekle...", en: "Please wait..." },
  invalidPhone: { tr: "Lütfen geçerli bir telefon numarası gir.", en: "Please enter a valid phone number." },
  phoneInvalid: { tr: "Geçersiz cep telefonu numarası.", en: "That's not a valid mobile number." },
  sendLimited: { tr: "Çok sık kod istendi. Biraz bekledikten sonra yeni kod alabilirsin.", en: "Too many codes requested. Wait a little, then ask for a new one." },
  smsFailed: { tr: "Tek kullanımlık doğrulama kodu gönderilemedi.", en: "The one-time code couldn't be sent." },
  connection: { tr: "Bağlantı hatası. Lütfen tekrar dene.", en: "Connection error. Please try again." },
  generic: { tr: "Bir hata oluştu.", en: "Something went wrong." },
  enterOtp: { tr: "Lütfen 4 haneli kodu gir.", en: "Please enter the 4-digit code." },
  invalidOtp: { tr: "Geçersiz kod.", en: "Invalid code." },
  expired: { tr: "Kodun süresi doldu. Yeni kod iste.", en: "The code has expired. Request a new one." },
  verifyLimited: {
    tr: "Çok fazla deneme oldu. Biraz bekledikten sonra yeniden giriş yapmayı deneyebilirsin.",
    en: "Too many attempts. Wait a little before trying to sign in again.",
  },
  sessionExpired: { tr: "Oturumun düştü. Numaranı tekrar girip yeni bir kod iste.", en: "Your session expired. Enter your number again and request a new code." },
  ageRequired: { tr: "Devam etmek için 13 yaşından büyük olduğunu onayla.", en: "Please confirm you're 13 or older to continue." },
  unavailable: {
    tr: "Telefonla giriş şu an kapalı. Oynamaya devam edebilirsin; ilerlemen bu cihazda duruyor.",
    en: "Phone sign-in is off right now. Keep playing; your progress is on this device.",
  },
  memberTitle: { tr: "Hesabın", en: "Your account" },
  memberPhone: { tr: "Telefonla giriş yaptın", en: "Signed in with your phone" },
  welcome: { tr: "Giriş yaptın. İlerlemen artık hesabında.", en: "You're signed in. Your progress is now in your account." },
  switched: { tr: "Bu numaradaki ilerlemen bu cihazdakiyle birleştirildi.", en: "Your progress on this number was merged with this device's." },
  syncSaved: {
    tr: "İlerlemen kaydedildi. Başka bir cihazda aynı numarayla giriş yapınca kaldığın yerden devam edersin.",
    en: "Your progress is saved. Sign in with the same number on another device to pick up where you left off.",
  },
  syncing: { tr: "Kaydediliyor…", en: "Saving…" },
  syncOffline: { tr: "Şu an kaydedilemedi; internet gelince kendiliğinden kaydedilecek.", en: "Couldn't save right now; it will save by itself once you're online." },
  whatsappNote: { tr: "İstediğin an kapatabilirsin; kapatman hiçbir şey kaybettirmez.", en: "Turn it off any time; you lose nothing." },
  whatsappFailed: { tr: "Seçimin kaydedilemedi. Lütfen tekrar dene.", en: "Your choice couldn't be saved. Please try again." },
  logout: { tr: "Çıkış yap", en: "Sign out" },
  loggingOut: { tr: "Çıkış yapılıyor...", en: "Signing out..." },
  logoutFailed: { tr: "Çıkış yapılamadı. Bağlantını kontrol edip tekrar dene.", en: "Couldn't sign out. Check your connection and try again." },
  loggedOut: { tr: "Çıkış yaptın. Bu cihazdaki ilerlemen duruyor.", en: "You're signed out. Your progress stays on this device." },
  done: { tr: "Tamam", en: "Done" },
} as const;

export type AccountTextKey = keyof typeof STRINGS;

export function accountText(key: AccountTextKey, lang: Lang, vars: Record<string, string | number> = {}): string {
  return STRINGS[key][lang].replace(/\{(\w+)\}/g, (_, k: string) => String(vars[k] ?? `{${k}}`));
}

/** The copy for an API answer to step 1 (send) or step 2 (verify). */
export function errorText(step: "send" | "verify", code: ApiErrorCode | "NETWORK", lang: Lang): string {
  const key: AccountTextKey =
    code === "NETWORK"
      ? "connection"
      : code === "PHONE_INVALID"
        ? "phoneInvalid"
        : code === "RATE_LIMITED"
          ? step === "send"
            ? "sendLimited"
            : "verifyLimited"
          : code === "SMS_FAILED"
            ? "smsFailed"
            : code === "CODE_INVALID"
              ? "invalidOtp"
              : code === "CODE_EXPIRED"
                ? "expired"
                : code === "AGE_REQUIRED"
                  ? "ageRequired"
                  : code === "NOT_FOUND"
                    ? "unavailable"
                    : code === "AUTH_REQUIRED" || code === "SESSION_EXPIRED"
                      ? step === "send"
                        ? "connection"
                        : "sessionExpired"
                      : "generic";
  return accountText(key, lang);
}

export interface AccountDialogOptions {
  lang: Lang;
  /** CSS colour of the primary button and focus rings (the game's accent). */
  accent?: string;
  onClose?: () => void;
}

/** The step being shown; kept while the page lives, so closing during the code step and reopening resumes it. */
interface Flow {
  step: "phone" | "code" | "member";
  age: boolean;
  phone: string;
  whatsapp: boolean;
  sentAt: number;
  seconds: number;
  notice?: { key: AccountTextKey; kind: "ok" | "error" };
}

const CODE_LIFETIME_MS = 120_000;
let flow: Flow = { step: "phone", age: false, phone: "", whatsapp: false, sentAt: 0, seconds: 120 };
let dialog: HTMLDialogElement | null = null;
let cleanup: (() => void)[] = [];
let options: AccountDialogOptions = { lang: "tr" };

type Child = Node | string | null | undefined | false;

function h<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Record<string, string | boolean | undefined> = {}, ...children: Child[]): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [name, value] of Object.entries(attrs)) {
    if (value === undefined || value === false) continue;
    node.setAttribute(name, value === true ? "" : value);
  }
  for (const child of children) if (child) node.append(child);
  return node;
}

const t = (key: AccountTextKey, vars?: Record<string, string | number>): string => accountText(key, options.lang, vars);

/** Light accents (yellow) take dark text, dark ones white. */
function onAccent(color: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(color.trim());
  if (!m) return "#1b1f3a";
  const n = parseInt(m[1]!, 16);
  const lum = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return lum > 0.6 ? "#1b1f3a" : "#ffffff";
}

function alertBox(): HTMLParagraphElement {
  return h("p", { class: "ej-acc-alert ej-acc-error", role: "alert", hidden: true });
}

function show(box: HTMLElement, text: string, kind: "ok" | "error" = "error"): void {
  box.textContent = text;
  box.className = `ej-acc-alert ej-acc-${kind}`;
  box.hidden = false;
}

function hide(box: HTMLElement): void {
  box.textContent = "";
  box.hidden = true;
}

function busy(button: HTMLButtonElement, label: string | null): void {
  if (label) {
    button.dataset.label = button.textContent ?? "";
    button.textContent = label;
    button.disabled = true;
  } else {
    button.textContent = button.dataset.label ?? button.textContent;
    button.disabled = false;
  }
}

/** Open the dialog for this page's account. Does nothing when the page has none or it is off. */
export function openAccountDialog(opts: AccountDialogOptions): void {
  const account = pageAccount();
  if (!account || account.current.status === "off" || account.current.status === "checking") return;
  options = opts;
  if (!dialog) {
    dialog = h("dialog", { class: "ej-acc", "aria-labelledby": "ej-acc-title" });
    // The games listen to the keyboard on window (Enter plays a level): typing here must not reach them.
    for (const type of ["keydown", "keyup", "keypress"] as const) dialog.addEventListener(type, (event) => event.stopPropagation());
    // A tap on the dimmed area outside the card closes it, as the native Escape does.
    dialog.addEventListener("click", (event) => {
      if (event.target === dialog) closeAccountDialog();
    });
    dialog.addEventListener("close", () => teardown());
    document.body.append(dialog);
  }
  const accent = opts.accent ?? "#ffca3a";
  dialog.style.setProperty("--ej-acc-accent", accent);
  dialog.style.setProperty("--ej-acc-on-accent", onAccent(accent));
  dialog.lang = opts.lang;
  if (account.current.status === "member") flow = { ...flow, step: "member" };
  else if (flow.step === "member" || (flow.step === "code" && Date.now() - flow.sentAt > CODE_LIFETIME_MS)) flow = { ...flow, step: "phone", notice: undefined };
  render(account);
  if (dialog.open) return;
  // Old web views without a modal <dialog>: shown in place (the CSS positions it over the page all the same).
  if (typeof dialog.showModal === "function") dialog.showModal();
  else dialog.setAttribute("open", "");
}

export function closeAccountDialog(): void {
  if (!dialog?.open) return;
  if (typeof dialog.close === "function") dialog.close();
  else {
    dialog.removeAttribute("open");
    teardown();
  }
}

function teardown(): void {
  for (const fn of cleanup) fn();
  cleanup = [];
  if (flow.step !== "code") flow = { ...flow, notice: undefined };
  options.onClose?.();
}

function render(account: Account): void {
  if (!dialog) return;
  for (const fn of cleanup) fn();
  cleanup = [];
  const card = h("div", { class: "ej-acc-card" });
  const close = h("button", { type: "button", class: "ej-acc-x", "aria-label": t("close") }, "×");
  close.addEventListener("click", () => closeAccountDialog());
  card.append(close);
  if (flow.step === "member") renderMember(card, account);
  else if (flow.step === "code") renderCode(card, account);
  else renderPhone(card, account);
  dialog.replaceChildren(card);
}

function title(text: string): HTMLHeadingElement {
  return h("h2", { id: "ej-acc-title", class: "ej-acc-title", tabindex: "-1" }, text);
}

// ------------------------------------------------------------------ step 1: 13+, phone, WhatsApp

function renderPhone(card: HTMLElement, account: Account): void {
  const notice = alertBox();
  if (flow.notice) show(notice, t(flow.notice.key), flow.notice.kind);
  const age = h("input", { type: "checkbox", name: "age", checked: flow.age });
  const ageHint = h("p", { class: "ej-acc-note", id: "ej-acc-age-hint" }, t("ageHint"));
  const phone = h("input", {
    id: "ej-acc-phone",
    class: "ej-acc-input",
    type: "tel",
    inputmode: "tel",
    autocomplete: "tel-national",
    placeholder: "5XX XXX XX XX",
    maxlength: "16",
    "aria-describedby": "ej-acc-age-hint",
  });
  phone.value = formatNational(flow.phone);
  const whatsapp = h("input", { type: "checkbox", name: "whatsapp", checked: flow.whatsapp });
  const error = alertBox();
  const submit = h("button", { type: "submit", class: "ej-acc-btn ej-acc-primary" }, t("continue"));
  const fields = h(
    "fieldset",
    { disabled: !flow.age },
    h("div", {}, h("label", { class: "ej-acc-label", for: "ej-acc-phone" }, t("phone")), h("div", { class: "ej-acc-phone-row" }, h("span", { class: "ej-acc-prefix", "aria-hidden": "true" }, "+90"), phone)),
    h("label", { class: "ej-acc-check" }, whatsapp, h("span", {}, t("whatsapp"))),
    error,
    submit,
  );
  const notNow = h("button", { type: "button", class: "ej-acc-link" }, t("notNow"));
  const form = h(
    "form",
    { novalidate: true },
    title(t("title")),
    notice,
    h("p", { class: "ej-acc-lead" }, t("lead")),
    h("label", { class: "ej-acc-check" }, age, h("span", {}, t("age"))),
    ageHint,
    fields,
    h("p", { class: "ej-acc-note" }, t("privacy")),
    notNow,
  );
  card.append(form);

  age.addEventListener("change", () => {
    flow = { ...flow, age: age.checked };
    fields.disabled = !age.checked;
    hide(error);
    if (age.checked) phone.focus();
  });
  phone.addEventListener("input", () => {
    // Re-mask only while typing at the end, so editing in the middle keeps the caret where it is.
    if (phone.selectionStart === phone.value.length) phone.value = formatNational(phone.value);
    flow = { ...flow, phone: nationalDigits(phone.value) };
    hide(error);
  });
  phone.addEventListener("blur", () => {
    phone.value = formatNational(phone.value);
  });
  whatsapp.addEventListener("change", () => {
    flow = { ...flow, whatsapp: whatsapp.checked };
  });
  notNow.addEventListener("click", () => closeAccountDialog());
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    void (async () => {
      hide(notice);
      hide(error);
      if (!age.checked) return show(error, t("ageRequired"));
      if (!isTurkishMobile(phone.value)) return show(error, t("invalidPhone"));
      flow = { ...flow, phone: nationalDigits(phone.value), whatsapp: whatsapp.checked, notice: undefined };
      busy(submit, t("sending"));
      const result = await account.sendCode(flow.phone, options.lang, flow.whatsapp);
      if (!submit.isConnected) return;
      busy(submit, null);
      if (!result.ok) return show(error, errorText("send", result.code, options.lang));
      flow = { ...flow, step: "code", sentAt: Date.now(), seconds: result.value.seconds || 120 };
      render(account);
    })();
  });
  requestAnimationFrame(() => (flow.age ? phone : (form.querySelector(".ej-acc-title") as HTMLElement | null))?.focus());
}

// ------------------------------------------------------------------ step 2: the 4-digit code

function renderCode(card: HTMLElement, account: Account): void {
  const change = h("button", { type: "button", class: "ej-acc-link" }, t("change"));
  const error = alertBox();
  const digits = [0, 1, 2, 3].map((i) =>
    h("input", {
      class: "ej-acc-input",
      type: "text",
      inputmode: "numeric",
      pattern: "[0-9]*",
      maxlength: i === 0 ? "4" : "1",
      autocomplete: "one-time-code",
      "aria-label": t("digit", { n: i + 1 }),
      "data-index": String(i),
    }),
  );
  const countdown = h("span", {});
  const resend = h("button", { type: "button", class: "ej-acc-link", disabled: true }, `${t("resend")} `, countdown);
  const submit = h("button", { type: "submit", class: "ej-acc-btn ej-acc-primary", disabled: true }, t("signIn"));
  const form = h(
    "form",
    { novalidate: true },
    title(t("title")),
    h(
      "div",
      { class: "ej-acc-phone-line" },
      h("div", {}, h("span", { class: "ej-acc-label" }, t("phone")), h("div", { class: "ej-acc-phone-value" }, displayPhone(flow.phone))),
      change,
    ),
    error,
    h("p", { class: "ej-acc-hint" }, t("otpHint")),
    h("div", { class: "ej-acc-otp" }, ...digits),
    resend,
    submit,
  );
  card.append(form);

  const code = () => digits.map((input) => input.value).join("");
  const refresh = () => {
    submit.disabled = code().length !== 4;
  };
  const fill = (from: number, text: string) => {
    const values = codeDigits(text, 4 - from);
    values.forEach((value, k) => {
      digits[from + k]!.value = value;
    });
    digits[Math.min(3, from + values.length)]?.focus();
    refresh();
  };
  digits.forEach((input, i) => {
    input.addEventListener("input", () => {
      const typed = input.value.replace(/\D/g, "");
      // An SMS autofill or a fast typist can put the whole code into one box: spread it.
      if (typed.length > 1) fill(i, typed);
      else {
        input.value = typed;
        if (typed && i < 3) digits[i + 1]!.focus();
        refresh();
      }
      hide(error);
    });
    input.addEventListener("keydown", (event) => {
      if (event.key === "Backspace" && !input.value && i > 0) {
        const previous = digits[i - 1]!;
        previous.value = "";
        previous.focus();
        refresh();
      }
    });
    input.addEventListener("paste", (event) => {
      event.preventDefault();
      fill(0, event.clipboardData?.getData("text") ?? "");
    });
  });

  const tick = () => {
    const left = Math.ceil((flow.sentAt + flow.seconds * 1000 - Date.now()) / 1000);
    resend.disabled = left > 0;
    countdown.textContent = left > 0 ? `(${left} ${t("seconds")})` : "";
  };
  tick();
  const timer = setInterval(tick, 1000);
  cleanup.push(() => clearInterval(timer));

  change.addEventListener("click", () => {
    flow = { ...flow, step: "phone", notice: undefined };
    render(account);
  });
  resend.addEventListener("click", () => {
    void (async () => {
      hide(error);
      busy(resend, t("sending"));
      const result = await account.sendCode(flow.phone, options.lang, flow.whatsapp, true);
      if (!resend.isConnected) return;
      busy(resend, null);
      resend.append(countdown);
      if (!result.ok) {
        tick();
        return show(error, errorText("send", result.code, options.lang));
      }
      flow = { ...flow, sentAt: Date.now(), seconds: result.value.seconds || 120 };
      tick();
      digits[0]!.focus();
    })();
  });
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    void (async () => {
      hide(error);
      if (code().length !== 4) return show(error, t("enterOtp"));
      busy(submit, t("wait"));
      const result = await account.verifyCode(flow.phone, code(), flow.whatsapp);
      if (!submit.isConnected) return;
      if (result.ok) {
        flow = { ...flow, step: "member", age: false, phone: "", notice: { key: result.value.switchedAccount ? "switched" : "welcome", kind: "ok" } };
        return render(account);
      }
      busy(submit, null);
      if (result.code === "CODE_EXPIRED") flow = { ...flow, sentAt: 0 };
      tick();
      show(error, errorText("verify", result.code, options.lang));
      for (const input of digits) input.value = "";
      refresh();
      digits[0]!.focus();
    })();
  });
  requestAnimationFrame(() => digits[0]!.focus());
}

// ------------------------------------------------------------------ signed in: phone, sync, WhatsApp, sign out

function renderMember(card: HTMLElement, account: Account): void {
  const notice = alertBox();
  if (flow.notice) show(notice, t(flow.notice.key), flow.notice.kind);
  const hint = h("strong", {});
  const sync = h("p", { class: "ej-acc-sync", "aria-live": "polite" });
  const whatsapp = h("input", { type: "checkbox", role: "switch" });
  const error = alertBox();
  const logout = h("button", { type: "button", class: "ej-acc-btn ej-acc-secondary" }, t("logout"));
  const done = h("button", { type: "button", class: "ej-acc-btn ej-acc-primary" }, t("done"));
  card.append(
    title(t("memberTitle")),
    notice,
    h("p", { class: "ej-acc-lead" }, `${t("memberPhone")}: `, hint),
    sync,
    h("label", { class: "ej-acc-switch" }, h("span", {}, t("whatsapp")), whatsapp),
    h("p", { class: "ej-acc-note" }, t("whatsappNote")),
    error,
    h("div", { class: "ej-acc-actions" }, logout, done),
  );

  let pending = false;
  const update = (state: AccountState) => {
    hint.textContent = state.viewer?.phoneHint ?? "";
    if (!pending) whatsapp.checked = state.viewer?.whatsappOptIn === true;
    whatsapp.setAttribute("aria-checked", String(whatsapp.checked));
    sync.textContent = state.sync === "syncing" ? t("syncing") : state.sync === "offline" ? t("syncOffline") : t("syncSaved");
  };
  cleanup.push(account.subscribe(update));

  whatsapp.addEventListener("change", () => {
    void (async () => {
      hide(error);
      const granted = whatsapp.checked;
      pending = true;
      whatsapp.disabled = true;
      const ok = await account.setWhatsapp(granted);
      pending = false;
      whatsapp.disabled = false;
      if (!ok) {
        whatsapp.checked = !granted;
        show(error, t("whatsappFailed"));
      }
      whatsapp.setAttribute("aria-checked", String(whatsapp.checked));
    })();
  });
  logout.addEventListener("click", () => {
    void (async () => {
      hide(error);
      busy(logout, t("loggingOut"));
      const ok = await account.logout();
      if (!logout.isConnected) return;
      if (!ok) {
        busy(logout, null);
        return show(error, t("logoutFailed"));
      }
      flow = { step: "phone", age: false, phone: "", whatsapp: false, sentAt: 0, seconds: 120, notice: { key: "loggedOut", kind: "ok" } };
      render(account);
    })();
  });
  done.addEventListener("click", () => closeAccountDialog());
  requestAnimationFrame(() => done.focus());
}
